import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import InfoIcon from "@mui/icons-material/Info";
import WarningIcon from "@mui/icons-material/Warning";
import ErrorIcon from "@mui/icons-material/Error";
import { FC } from "react";
import { useTheme } from "@mui/material";
import { getTempZipFileUrl } from "@/actions/importActions";
import { useRouter } from "next/navigation";
import { useSelect } from "@/context/ContextSelector";
import { addContextToUrl } from "@/utils/contextUrl";

type ConfirmationDialogProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
  type: "success" | "info" | "warning" | "error";
  onClose?: () => void;
  onConfirm?: () => void;
  hasCancelButton?: boolean;
  title: string;
  content: string;
  showDownloadButton?: boolean;
  filePathList?: string;
  hasLoginButton?: boolean
};

export const ConfirmationDialog: FC<ConfirmationDialogProps> = ({
  open,
  setOpen,
  onClose,
  onConfirm,
  hasCancelButton,
  title,
  type,
  content,
  showDownloadButton,
  filePathList,
  hasLoginButton
}) => {
  const theme = useTheme();
  const router = useRouter();
  const { contextValue } = useSelect();

  const handleClose = () => {
    onClose && onClose();
    setOpen(false);
  };

  const handleConfirm = () => {
    onConfirm && onConfirm();
    setOpen(false);
  };

  const handleDownloadFiles = async (filePathList: string) => {
    try {
      const zipUrl = await getTempZipFileUrl(
        filePathList, "aln_error",
        true
      );
      const link = document.createElement('a');
      link.href = zipUrl;
      link.setAttribute('download', filePathList.split("/")[1]);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error downloading aln_error zip file:", error);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="xs">
        {type === "success" && (
          <CheckCircleOutlineIcon
            fontSize="large"
            sx={{
              color: theme.palette.success.main,
              margin: "auto",
              mt: 4,
            }}
          />
        )}
        {type === "info" && (
          <InfoIcon
            fontSize="large"
            sx={{
              color: theme.palette.info.main,
              margin: "auto",
              mt: 4,
            }}
          />
        )}
        {type === "warning" && (
          <WarningIcon
            fontSize="large"
            sx={{
              color: theme.palette.warning.main,
              margin: "auto",
              mt: 4,
            }}
          />
        )}
        {type === "error" && (
          <ErrorIcon
            fontSize="large"
            sx={{
              color: theme.palette.error.main,
              margin: "auto",
              mt: 4,
            }}
          />
        )}
        <DialogTitle
          id="success-dialog-title"
          variant="subtitle1"
          sx={{ textAlign: "center" }}
        >
          {title}
        </DialogTitle>
        <DialogContent>
          <DialogContentText variant="body2" id="success-dialog-description" sx={{ whiteSpace: 'pre-line' }}>
            {content}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "space-between" }}>
          {showDownloadButton && filePathList && (
            <Button variant="contained" onClick={() => handleDownloadFiles(filePathList)} color="error">
              Download Aligned Files
            </Button>
          )}
          <Button onClick={handleConfirm} autoFocus>
            {hasCancelButton ? `Ok` : `Close`}
          </Button>
          {hasCancelButton && (
            <Button onClick={handleClose} autoFocus>
              Cancel
            </Button>
          )}
          {hasLoginButton && (
            <Button onClick={() => router.push(addContextToUrl("/login", contextValue))} autoFocus>
              Login
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};
